import React, { useRef, useState, useEffect, useCallback } from 'react';
import MicrodepositVerificationForm from "./MicrodepositVerificationForm";
import LoadingScreen from './LoadingScreen';
import SvsBanner from './SvsBanner';
import { initValidationFallback, pollValidateAccount, checkSessionStatus } from '../../redux/clientActions';
import {
  AIS,
  AVS,
  MICRODEPOSIT,
  CONFIRMATION,
  LOADING,
  SUCCESS,
  FAILURE,
  AWAITING_REDIRECT,
  AIS_SUCCESS,
  AIS_FAILURE,
  AIS_CONSENT_INITIALIZED,
  AIS_COMPONENT_TIMEOUT
} from '../JwtConstants';
import { JPMC_AIS } from '../ProviderConstants';
import ConfirmationScreen from "./ConfirmationScreen";
import { Logo } from '@jpmuitk/logo';
import AisConsentFrame from './AisConsentFrame';

const Wrapper = ({ children, sessionToken, accessToken, accountRegion }) => (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
    <SvsBanner sessionToken={sessionToken} accessToken={accessToken} accountRegion={accountRegion} />
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: '1', overflow: 'auto' }}>
      {children}
    </div>
    <footer style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px', background:
        '#D9DDE3'
    }}>
      Powered by <Logo />
    </footer>
  </div>
);

export default function SvsFlow({ accessToken, sessionToken, successRedirectUrl, failureRedirectUrl, initialRedirectUrl,
  profileComponents, accountRegion, initialRenderedComponent, onProfileComponentsChange }) {
  const [renderedComponent, setRenderedComponent] = useState(initialRenderedComponent);
  const [redirectUrl, setRedirectUrl] = useState(initialRedirectUrl && initialRedirectUrl.trim() !== "" ? initialRedirectUrl : "");
  const [maskedAccount, setMaskedAccount] = useState("");
  // Absolute wall-clock instant (epoch millis) the flow expires, from the server-authoritative
  // ValidationResponse. Passed to the microdeposit form so its countdown targets a fixed instant that
  // survives tab throttling and close/reopen (AVS-11068).
  const [effectiveExpiresAt, setEffectiveExpiresAt] = useState(null);
  const [terminalOutcome, setTerminalOutcome] = useState(null);
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    handleValidateAccountResponse(false);
  }, []);

  const handleAisResponse = (response) => {
    if (
      !response
      || response.length === 0
      || response.status === AIS_FAILURE
      || response.status === AIS_COMPONENT_TIMEOUT
    ) {
      if (profileComponents.length > 1) {
        handleValidateAccountResponse(true).then();
      } else if (response?.status === AIS_COMPONENT_TIMEOUT) {
        handleAisErrorResponse(9001, "Customer did not complete flow", SUCCESS).then();
        goToConfirmation(SUCCESS);
      } else {
        if (response?.code && response?.description) {
          handleAisErrorResponse(response.code, response.description, FAILURE).then();
        } else if (response?.description) {
          handleAisErrorResponse(null, response.description, FAILURE).then();
        } else {
          handleAisErrorResponse(9001, "Unexpected Error", FAILURE).then();
        }
        goToConfirmation(FAILURE);
      }
    } else if (response.status === AIS_SUCCESS) {
      goToConfirmation(SUCCESS);
    } else if (response.status === AIS_CONSENT_INITIALIZED) {
      setRenderedComponent(AIS);
    } else {
      setRenderedComponent(AIS);
    }
  };

  const handleSetIsRedirecting = (value) => {
    isRedirectingRef.current = value;
  };

  // Transition to the confirmation/redirect screen, recording the terminal outcome so the
  // ConfirmationScreen can signal the backend before navigating to the pass/fail URL.
  const goToConfirmation = (outcome) => {
    setTerminalOutcome(outcome);
    setRedirectUrl(outcome === SUCCESS ? successRedirectUrl : failureRedirectUrl);
    setRenderedComponent(CONFIRMATION);
  };

  const handleAisErrorResponse = async (code, description, terminalState) => {
    await initValidationFallback(accessToken, sessionToken, code, description, JPMC_AIS, terminalState);
  };

  const handleValidateAccountResponse = async (isTransition) => {
    try {
      let activeComponents = [...profileComponents];

      // If transitioning from AIS to next component, remove the first component
      if (isTransition && activeComponents.length > 1) {
        activeComponents.shift();
        onProfileComponentsChange?.(activeComponents);
      }

      // === UNIFIED SERVER-SIDE GUARD (all flows) ===
      if (!isTransition) {
        const status = await checkSessionStatus(accessToken, sessionToken);
        if (status) {
          const { component, lastFour, pendingOutcome, effectiveExpiresAt: statusExpiresAt } = status;
          if (statusExpiresAt != null) {
            setEffectiveExpiresAt(statusExpiresAt);
          }

          // Terminal state -> CONFIRMATION
          if (component === SUCCESS || component === FAILURE) {
            goToConfirmation(component);
            return;
          }

          // Deferred outcome awaiting the redirect signal -> CONFIRMATION with the real outcome
          if (component === AWAITING_REDIRECT) {
            goToConfirmation(pendingOutcome === SUCCESS ? SUCCESS : FAILURE);
            return;
          }

          // MICRODEPOSIT → show form directly
          if (component === MICRODEPOSIT) {
            setMaskedAccount(lastFour);
            setRenderedComponent(MICRODEPOSIT);
            return;
          }

          // INIT / AVS → session has started and is mid-validation (AIS may be done if it's
          // in profile). AVS is a non-terminal in-progress marker written to the cache while the
          // provider result is pending; treat it like INIT so we keep polling for a terminal state
          // instead of stalling on LOADING or re-showing AIS.
          if (component === 'INIT' || component === AVS) {
            if (activeComponents.includes(AIS)) {
              activeComponents = activeComponents.filter(c => c !== AIS);
              onProfileComponentsChange?.(activeComponents);
            }
            // fall through to non-AIS handling below
          }
        }
        // status is null (204 or error) → first visit, fall through
      }

      // === RENDER FIRST COMPONENT ===
      if (activeComponents.includes(AIS)) {
        setRenderedComponent(AIS);
        return;
      }

      // === NON-AIS: POLL FOR STATE ===
      if (activeComponents.includes(AVS) || activeComponents.includes(MICRODEPOSIT)) {
        setRenderedComponent(LOADING);
        const resp = await pollValidateAccount(accessToken, sessionToken);
        if (!resp || resp.length === 0) {
          goToConfirmation(FAILURE);
          return;
        }

        const component = resp[0].component;
        setMaskedAccount(resp[0].lastFour);
        if (resp[0].effectiveExpiresAt != null) {
          setEffectiveExpiresAt(resp[0].effectiveExpiresAt);
        }

        if (component === SUCCESS) {
          goToConfirmation(SUCCESS);
        } else if (component === MICRODEPOSIT) {
          setRenderedComponent(MICRODEPOSIT);
        } else if (component === FAILURE) {
          goToConfirmation(FAILURE);
        } else if (component === AWAITING_REDIRECT) {
          goToConfirmation(resp[0].pendingOutcome === SUCCESS ? SUCCESS : FAILURE);
        }
      }
    } catch (error) {
      console.error(error);
      goToConfirmation(FAILURE);
    }
  };

  const renderComponent = () => {
    switch (renderedComponent) {
      case AIS:
        return (
          <AisConsentFrame
            accessToken={accessToken}
            sessionToken={sessionToken}
            accountRegion={accountRegion}
            onResponse={handleAisResponse}
          />
        );

      case LOADING:
        return <LoadingScreen />;

      case CONFIRMATION:
        return (
          <ConfirmationScreen
            redirectUrl={redirectUrl}
            setIsRedirecting={handleSetIsRedirecting}
            outcome={terminalOutcome}
            accessToken={accessToken}
            sessionToken={sessionToken}
          />
        );

      case MICRODEPOSIT:
        return (
          <MicrodepositVerificationForm
            accessToken={accessToken}
            sessionToken={sessionToken}
            maskedAccount={maskedAccount}
            successRedirectUrl={successRedirectUrl}
            failureRedirectUrl={failureRedirectUrl}
            handleSetIsRedirecting={handleSetIsRedirecting}
            effectiveExpiresAt={effectiveExpiresAt}
            accountRegion={accountRegion}
          />
        );
      default:
        return <LoadingScreen />;
    }
  }

  return (
    <Wrapper sessionToken={sessionToken} accessToken={accessToken} accountRegion={accountRegion}>
      {renderComponent()}
    </Wrapper>
  );
}
